import { useEffect, useRef, type MutableRefObject } from "react";

export type AlarmLevel = "normal" | "warning" | "critical";

interface AlarmManagerProps {
  level: AlarmLevel;
  criticalCount?: number;
  soundEnabled?: boolean;
  flashEnabled?: boolean;
  titleEnabled?: boolean;
  baseTitle?: string;
}

type AudioCtx = InstanceType<typeof AudioContext>;

function getAudioCtor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") return undefined;
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
}

function flashBody() {
  if (typeof document === "undefined") return;
  document.body.classList.remove("alarm-active");
  void document.body.offsetWidth; // reflow — ayni animasyonu tekrar tetiklemek icin
  document.body.classList.add("alarm-active");
}

function playBeep(ctxRef: MutableRefObject<AudioCtx | null>) {
  try {
    const Ctor = getAudioCtor();
    if (!Ctor) return;
    ctxRef.current ??= new Ctor();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") void ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    // Autoplay engeli / desteklenmeyen tarayici — sessizce yut (js/alerts.js ile ayni davranis).
  }
}

/**
 * Alarm sistemi — ses + ekran flasi + sekme basligi (eski js/alerts.js'in
 * React karsiligi). Tetiklenme SADECE seviye/kritik sayisi DEGISTIGINDE
 * olur (surekli degil); ayarlardan ac/kapa bayraklari prop olarak gelir.
 * Gorsel cikti yok — yan etkiler icin headless bilesen.
 */
export function AlarmManager({
  level,
  criticalCount = 0,
  soundEnabled = true,
  flashEnabled = true,
  titleEnabled = true,
  baseTitle = "AsakAI",
}: AlarmManagerProps) {
  const lastLevelRef = useRef<AlarmLevel | null>(null);
  const lastCountRef = useRef(0);
  const audioCtxRef = useRef<AudioCtx | null>(null);
  const primedRef = useRef(false);

  // Autoplay politikasi: AudioContext yalniz ilk kullanici etkilesiminden sonra "isitilir".
  useEffect(() => {
    function prime() {
      if (primedRef.current) return;
      try {
        const Ctor = getAudioCtor();
        if (!Ctor) return;
        audioCtxRef.current ??= new Ctor();
        if (audioCtxRef.current.state === "suspended") void audioCtxRef.current.resume();
        primedRef.current = true;
      } catch {
        // yut
      }
    }
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, []);

  useEffect(() => {
    const changed = lastLevelRef.current !== level || lastCountRef.current !== criticalCount;
    if (changed) {
      const becameWorse =
        level === "critical" && (lastLevelRef.current !== "critical" || criticalCount > lastCountRef.current);
      if (becameWorse) {
        if (soundEnabled && primedRef.current) playBeep(audioCtxRef);
        if (flashEnabled) flashBody();
      }
      lastLevelRef.current = level;
      lastCountRef.current = criticalCount;
    }
  }, [level, criticalCount, soundEnabled, flashEnabled]);

  useEffect(() => {
    if (!titleEnabled) {
      document.title = baseTitle;
      return;
    }
    document.title = level === "critical" && criticalCount > 0 ? `(${criticalCount}) ${baseTitle}` : baseTitle;
  }, [level, criticalCount, titleEnabled, baseTitle]);

  return null;
}
